/**
 * Context-fade for the single-room floor-plan view: fade nearby structure
 * walls from *other* rooms by their distance to the selected room's floor
 * polygon, so the room is shown in the context of its surroundings.
 *
 * Pure geometry only — no canvas/React. See
 * claude-docs/2026-05-29-nearby-wall-context-fade-design.md.
 */

import { nearestPointOnSegment } from './roomCreationUtils';

/** Solid out to here (1 ft), then ramp to invisible by FADE_OUTER (2 ft). */
export const CONTEXT_FADE_INNER = 0.3048;
export const CONTEXT_FADE_OUTER = 0.6096;

/**
 * Minimum distance from point `p` ({x,z}) to a polygon's boundary (the min
 * over its edges of the point-to-segment distance). Polygon corners may carry
 * a `y`; only `x`/`z` are used. Returns Infinity for a degenerate polygon.
 */
export function distancePointToPolygon(p, polygon) {
  if (!polygon || polygon.length < 2) return Infinity;
  let best = Infinity;
  for (let i = 0; i < polygon.length; i++) {
    const a = polygon[i];
    const b = polygon[(i + 1) % polygon.length];
    const np = nearestPointOnSegment(p, a, b);
    const d = Math.hypot(p.x - np.x, p.z - np.z);
    if (d < best) best = d;
  }
  return best;
}

/** Distance → alpha: 1 at/inside `inner`, linear ramp to 0 at `outer`. */
function fadeAlpha(d, inner, outer) {
  if (d <= inner) return 1;
  if (d >= outer) return 0;
  return 1 - (d - inner) / (outer - inner);
}

/**
 * Sample a wall (start→end, {x,z}) and map each sample's distance to the room
 * polygon to a fade alpha. Returns `{ visible, stops }` where `stops` is a list
 * of `{ t (0..1 along the wall), alpha }` — fed to a linear gradient so the
 * wall is solid where it hugs the room and fades to nothing as it recedes.
 * `visible` is false when every sample is beyond `fadeOuter`.
 */
export function wallContextFadeStops(
  start,
  end,
  polygon,
  {
    fadeInner = CONTEXT_FADE_INNER,
    fadeOuter = CONTEXT_FADE_OUTER,
    samples = 5,
  } = {}
) {
  const n = Math.max(2, samples);
  const stops = [];
  for (let i = 0; i < n; i++) {
    const t = i / (n - 1);
    const p = {
      x: start.x + (end.x - start.x) * t,
      z: start.z + (end.z - start.z) * t,
    };
    const d = distancePointToPolygon(p, polygon);
    stops.push({ t, alpha: fadeAlpha(d, fadeInner, fadeOuter) });
  }
  return { visible: stops.some((s) => s.alpha > 0), stops };
}
